import React, { useMemo, useReducer } from 'react';
import { observer } from 'mobx-react';
import cn from 'classnames';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { IconButton } from 'ksnm-common-ui/lib/IconButton';
import { Modal } from 'ksnm-common-ui/lib/Modal';
import { Table } from 'ksnm-common-ui/lib/Table2';
import { FILTER } from 'ksnm-common-ui/lib/Table2/utils/TableRequest/TableRequest.interface';
import { Tooltip } from 'ksnm-common-ui/lib/Tooltip';
import type { CSSProperties } from 'react';

import { constantStore } from '@stores/constantStore';
import { ksnmTableStore } from '@stores/ksnmTableStore/ksnmTableStore';
import { logsStore } from '@stores/logsStore';

import { getLogsColumns } from '../columns';
import type { IProcess } from '../Logs';

import styles from './LogsModal.module.scss';

const MASK_STYLES = { zIndex: 1011 };

interface IPreparingProtocolModalModal {
  open: boolean;
  onClose: () => void;
  process?: IProcess | null;
}

const { TABLE_NAMES } = constantStore;

export const LogsModal = observer(({ open, onClose, process }: IPreparingProtocolModalModal) => {
  const [fullSize, toggleFullSize] = useReducer((fullSizeState) => !fullSizeState, false);

  const { guid, reportPackageCode, jsonObjects } = process || {};

  const hardFilter = [[{ type: FILTER.EQ, name: 'etl_guid', values: [guid], filterIsInverted: false }]];

  const tableKey = `${guid}-${fullSize}`;

  const extendTableButtons = useMemo(
    () => (
      <IconButton
        title={fullSize ? 'Уменьшить' : 'Увеличить'}
        onClick={toggleFullSize}
        iconName={fullSize ? 'fullScreenOpen' : 'fullScreenClose'}
      />
    ),
    [fullSize],
  );

  const modalStyle = useMemo<CSSProperties>(() => ({ height: fullSize ? '98vh' : '80vh' }), [fullSize]);

  const tableName = fullSize ? `${TABLE_NAMES.LOGS_DETAILS}-fullSize` : TABLE_NAMES.LOGS_DETAILS;

  const columns = useMemo(() => getLogsColumns(fullSize), [fullSize]);

  const reportPackage = useMemo(() => {
    const content = Array.isArray(jsonObjects) && jsonObjects?.map((item) => item.reportVersionCode).join(', ');

    return (
      <div className={styles.package}>
        {`Код пакета: ${reportPackageCode}`}
        {content && (
          <Tooltip content={content} placement="auto">
            <div
              onClick={(e) => {
                e.stopPropagation();

                logsStore.setAdditionalOptions({
                  guid: guid ?? '',
                  reportPackageCode: reportPackageCode ?? '',
                  reportVersions: content,
                });
              }}
            >
              <Icon name="info" />
            </div>
          </Tooltip>
        )}
      </div>
    );
  }, [guid, jsonObjects, reportPackageCode]);

  return (
    <Modal
      open={open}
      className={cn(styles.modal, { [styles.fullSize]: fullSize })}
      wrapClassName={styles.wrapModal}
      maskStyle={MASK_STYLES}
      centered
      footer={null}
      onCancel={onClose}
      width={fullSize ? '98vw' : 1400}
      closeIcon={<Icon name="close" noFill height={24} />}
      destroyOnClose
      style={modalStyle}
    >
      <div className={styles.container}>
        <div className={styles.title}>
          <h4>
            GUID: {guid} <br />
            {reportPackageCode ? reportPackage : ''}
          </h4>
        </div>

        <Table
          tableName={tableName}
          columns={columns}
          resource="log.etl_log"
          externalFetch={ksnmTableStore.externalFetch}
          externalFetchXLSX={ksnmTableStore.getExcelStreamV2}
          cellHeight={42}
          hardFilter={hardFilter}
          extendTableButtons={extendTableButtons}
          key={tableKey}
          keyField="guid"
        />
      </div>
    </Modal>
  );
});
